export const histologyModule = {
  id: "histology",
  title: "Thyroid Histology",
  sections: [
    // ── Follicles, colloid & follicular cells ───────────────────────────────
    {
      type: "text",
      title: "Histology",
      content: `<h3>Thyroid Histology</h3>
     <p>Histologically the thyroid gland is made up of spherical structures called
     <strong>follicles</strong>, which are lined with a single layer of
     <span class="glossary-term">epithelial cells<span class="tooltip">
     Cells that line the surfaces of the body, including organs and glands.</span></span>
     called <b class="purple-term">follicular cells</b> (also called thyrocytes).</p>
     <p>The follicles are filled with a substance called <b class="purple-term">colloid</b>,
     which contains <strong>thyroglobulin</strong> — a glycoprotein produced by the follicular
     cells that serves as the precursor to thyroid hormones T3 and T4.</p>
     <p>The follicular cells can vary in shape depending on their activity level. When the thyroid
     is <strong>inactive</strong>, the follicular cells are <strong>cuboidal</strong>. When
     <strong>active</strong>, they become <strong>columnar</strong>.</p>
     <p>These follicular cells take up iodine from the bloodstream and move it into the follicle
     lumen, where it combines with thyroglobulin to form the precursors of T3 and T4. The
     follicular cells then endocytose the iodinated thyroglobulin from the colloid, cleave it
     with proteases, and release T3 and T4 into the bloodstream.</p>
     <p>Between the follicles are connective tissue septa carrying capillaries and lymphatics.
     These septa extend inward from the fibrous capsule and divide the gland into irregular
     <strong>lobules</strong>, each containing 20–40 follicles.</p>
      `,
      image: "/images/thyroid/thyroidhistology2.png",
    },
    {
      type: "question",
      text: "What is the substance called that fills the follicles?",
      options: ["Calcitonin", "Parafollicular cells", "Colloid", "Osteoclasts"],
      correct: 2,
      explanation:
        "The colloid is a viscous substance that fills the thyroid follicles. It contains iodinated thyroglobulin, the glycoprotein precursor to thyroid hormones T3 and T4.",
      explanationImage: "/images/thyroid/thyroidhistology1.png",
    },
    {
      type: "question",
      text: "What does it mean if a follicular cell is cuboidal?",
      options: [
        "The follicular cell is inactive",
        "The follicular cell is active",
      ],
      correct: 0,
      explanation:
        "Cuboidal follicular cells indicate an inactive thyroid. When stimulated (e.g. by TSH), the cells become taller and columnar, reflecting increased hormone synthesis and secretion.",
      explanationImage: "/images/thyroid/thyroidhistology1.png",
    },

    // ── Parafollicular cells (C cells) ──────────────────────────────────────
    {
      type: "text",
      title: "Histology",
      content: `<h3>Parafollicular Cells (C Cells)</h3>
      <p>Scattered within the thyroid — either within the follicular basement membrane but
      <em>outside</em> the follicular lumen, or in the interfollicular spaces — are
      <b class="purple-term">parafollicular cells</b> (also known as C cells).
      They appear <strong>pale</strong> compared to follicular cells on H&E staining.</p>
      <p>C cells produce the hormone <strong>calcitonin</strong>, which <b>lowers</b> blood
      calcium levels by:</p>
      <ul>
        <li><b>Inhibiting</b> <span class="glossary-term">osteoclast<span class="tooltip">Cells
        that break down bone and release calcium into the blood.</span></span> activity,
        reducing calcium release from bone</li>
        <li><b>Promoting</b> excretion of calcium and phosphate in the kidneys</li>
      </ul>
      <p>C cells are clinically important because <strong>medullary thyroid carcinoma (MTC)</strong>
      arises from these cells. MTC produces excess calcitonin, making serum calcitonin an important
      <strong>tumor marker</strong>. MTC also characteristically shows <strong>amyloid deposits</strong>
      in the stroma on histology.</p>
      `,
      image: "/images/thyroid/thyroidhistology2.png",
    },
    {
      type: "question",
      text: "What is the name of the hormone produced by parafollicular cells?",
      options: [
        "Calcitonin",
        "Parathyroid hormone",
        "Thyroxine",
        "Calcitriol",
      ],
      correct: 0,
      explanation:
        "Parafollicular cells (C cells) produce the hormone calcitonin, which inhibits osteoclast activity and promotes renal excretion of calcium, resulting in lower blood calcium levels.",
      explanationImage: "/images/thyroid/thyroidhistology1.png",
    },
    {
      type: "question",
      text: "How does calcitonin affect blood calcium levels?",
      options: [
        "Increases blood calcium levels",
        "Decreases blood calcium levels",
        "No effect on blood calcium levels",
        "Converts calcium to phosphate",
      ],
      correct: 1,
      explanation:
        "Calcitonin lowers blood calcium levels by inhibiting osteoclast activity (reducing calcium release from bone) and promoting renal excretion of calcium and phosphate.",
      explanationImage: "/images/thyroid/thyroidhistology1.png",
    },
    {
      type: "question",
      text: "Where are parafollicular cells (C cells) located in the thyroid?",
      options: [
        "Inside the follicle lumen, mixed with the colloid",
        "Within the follicular basement membrane or in the interfollicular spaces, outside the follicle lumen",
        "Only in the fibrous capsule of the thyroid",
        "Along the inner surface of the thyroid arteries",
      ],
      correct: 1,
      explanation:
        "C cells are located within the follicular basement membrane but outside the follicular lumen, or in the interfollicular stroma. They are never found inside the colloid-filled lumen.",
    },

    // ── Summary mnemonic ────────────────────────────────────────────────────
    {
      type: "text",
      title: "Histology",
      content: `
      <div class="mnemonic-card">
        <div class="mnemonic-title">Thyroid histology summary</div>
        <div class="mnemonic-content">
        <ul>
        <li><b class="purple-term">Follicular cells</b> (thyrocytes): cuboidal when inactive,
        columnar when active; produce T3 and T4 from iodinated thyroglobulin in the colloid.
        </li>
        <li><b class="purple-term">Colloid</b>: fills follicle lumen; contains iodinated
        thyroglobulin, the precursor to T3 and T4.
        </li>
        <li><b class="purple-term">Parafollicular cells</b> (C cells): pale-staining; outside
        follicle lumen; produce calcitonin → lowers blood calcium.
        </li>
        </ul>
        </div>
      </div>
      `,
      image: "/images/thyroid/thyroidhistology1.png",
    },
    {
      type: "question",
      text: "What does colloid contain?",
      options: [
        "T3 and T4 already in final form",
        "Iodinated thyroglobulin — the precursor to T3 and T4",
        "Calcitonin stored for release",
        "Thyroid stimulating hormone (TSH)",
      ],
      correct: 1,
      explanation:
        "The colloid contains iodinated thyroglobulin, which is the precursor to T3 and T4. Follicular cells endocytose the colloid, cleave the thyroglobulin with proteases, and release T3 and T4 into the bloodstream.",
      explanationImage: "/images/thyroid/thyroidhistology1.png",
    },
    {
      type: "question",
      text: "What hormones do the parafollicular cells produce?",
      options: [
        "Thyroxine (T4)",
        "Parathyroid hormone (PTH)",
        "Triiodothyronine (T3)",
        "Calcitonin",
      ],
      correct: 3,
      explanation:
        "Parafollicular cells (C cells) are found in the interfollicular spaces and produce calcitonin. Calcitonin inhibits osteoclast activity (reducing bone breakdown and calcium release into the blood) and increases renal excretion of calcium, thereby lowering blood calcium levels.",
      explanationImage: "/images/thyroid/thyroidhistology1.png",
    },

    // ── H&E staining appearance ─────────────────────────────────────────────
    {
      type: "text",
      title: "Histology",
      content: `<h3>Thyroid Appearance on H&E Staining</h3>
      <p>On <strong>haematoxylin and eosin (H&E)</strong> staining, the thyroid has a
      characteristic appearance that makes it easy to identify:</p>
      <p>The <strong>follicles</strong> appear as round to oval structures of varying sizes,
      filled with <b class="purple-term">eosinophilic (pink) colloid</b>. Colloid stains pink
      because it is a protein-rich substance (thyroglobulin) that binds eosin.</p>
      <p>The <strong>follicular cells</strong> lining each follicle appear as a single layer of
      <strong>low cuboidal epithelium</strong> in a resting thyroid, with nuclei positioned
      basally.</p>
      <p>The <strong>C cells (parafollicular cells)</strong> are scattered between follicles.
      They are larger than follicular cells and appear <strong>pale</strong> on H&E, as they do
      not take up eosin as intensely.</p>
      <p>Between the follicles, thin strands of <strong>connective tissue</strong> carry
      capillaries, allowing direct delivery of thyroid hormones into the bloodstream.</p>
      <div class="highlight-box">H&E appearance at a glance:
        <ul>
          <li>Follicles: <em>round structures filled with pink (eosinophilic) colloid</em></li>
          <li>Follicular cells: <em>single layer of low cuboidal epithelium lining the follicle</em></li>
          <li>C cells: <em>larger, pale-staining cells in the interfollicular spaces</em></li>
          <li>Connective tissue + capillaries: <em>between follicles</em></li>
        </ul>
      </div>`,
      image: "/images/thyroid/thyroidhistology1.png",
    },
    {
      type: "question",
      text: "What color does colloid stain on H&E?",
      options: [
        "Blue (basophilic)",
        "Pink (eosinophilic)",
        "Purple",
        "Unstained (clear)",
      ],
      correct: 1,
      explanation:
        "Colloid stains pink (eosinophilic) on H&E because it is a protein-rich substance (thyroglobulin) that binds eosin. This is a key feature used to identify thyroid tissue histologically.",
    },
    {
      type: "question",
      text: "How do C cells (parafollicular cells) appear on H&E staining compared to follicular cells?",
      options: [
        "Darker and smaller",
        "Identical in appearance",
        "Larger and pale-staining",
        "Basophilic and columnar",
      ],
      correct: 2,
      explanation:
        "C cells are larger than follicular cells and appear pale on H&E staining. They are located in the interfollicular spaces or within the follicular basement membrane, outside the follicle lumen.",
    },

    // ── Active vs. inactive thyroid / colloid scalloping ───────────────────
    {
      type: "text",
      title: "Histology",
      content: `<h3>Active vs. Inactive Thyroid — Colloid Scalloping</h3>
      <p>The microscopic appearance of the thyroid changes depending on how active it is:</p>
      <p>In an <strong>inactive thyroid</strong> (e.g. low TSH):
      <ul>
        <li>Follicular cells are <strong>flat and cuboidal</strong></li>
        <li>Follicles are <strong>large and distended</strong> with abundant colloid</li>
        <li>The colloid margin is <strong>smooth</strong></li>
      </ul></p>
      <p>In an <strong>active thyroid</strong> (e.g. high TSH, Graves' disease):
      <ul>
        <li>Follicular cells are <strong>tall and columnar</strong></li>
        <li>Follicles are <strong>smaller</strong> with reduced colloid</li>
        <li>The edge of the colloid shows <b class="purple-term">scalloping</b> (resorption
        vacuoles) — small indentations at the apical surface of follicular cells where the cells
        are actively endocytosing colloid for hormone release</li>
      </ul></p>
      <div class="mnemonic-card">
        <div class="mnemonic-title">Active vs. inactive thyroid on histology</div>
        <div class="mnemonic-content">
          <ul>
            <li><b class="purple-term">Active</b>: tall columnar cells + small follicles +
            <b>scalloped</b> colloid edge</li>
            <li><b class="purple-term">Inactive</b>: flat cuboidal cells + large follicles +
            <b>smooth</b> colloid edge</li>
          </ul>
        </div>
      </div>`,
    },
    {
      type: "question",
      text: "What is colloid scalloping (resorption vacuoles)?",
      options: [
        "Smooth, rounded colloid filling a large resting follicle",
        "Indentations at the apical surface of follicular cells indicating active endocytosis of colloid",
        "Deposition of amyloid within the colloid",
        "Calcification of the colloid in chronic hypothyroidism",
      ],
      correct: 1,
      explanation:
        "Colloid scalloping refers to indentations (resorption vacuoles) at the apical surface of follicular cells, created as they actively endocytose colloid to release thyroid hormones. It is a histological sign of an active, hyperstimulated thyroid.",
    },
    {
      type: "question",
      text: "What would you expect to see histologically in a thyroid with very high TSH levels?",
      options: [
        "Flat cuboidal cells, large follicles distended with smooth colloid",
        "Tall columnar cells, small follicles, and scalloped colloid",
        "Follicle destruction with lymphocytic infiltration",
        "Amyloid deposits in the stroma",
      ],
      correct: 1,
      explanation:
        "High TSH strongly stimulates the follicular cells, causing them to become tall and columnar. The follicles shrink as colloid is consumed, and scalloping of the colloid margin appears, indicating active endocytosis.",
    },
    {
      type: "question",
      text: "What would you expect to see histologically in a thyroid with very low TSH levels?",
      options: [
        "Tall columnar cells, small follicles, scalloped colloid",
        "Flat cuboidal cells, large follicles distended with smooth colloid",
        "Lymphocytic infiltration and germinal centers",
        "C cell hyperplasia and amyloid",
      ],
      correct: 1,
      explanation:
        "Without TSH stimulation, follicular cells are flat and cuboidal. The follicles become large and engorged with colloid, which has a smooth margin with no scalloping, indicating minimal hormone secretion.",
    },

    // ── Clinical histology correlations ─────────────────────────────────────
    {
      type: "text",
      title: "Histology",
      content: `<h3>Clinical Histology Correlations</h3>
      <p>Understanding thyroid histology helps interpret the appearance of common thyroid diseases:</p>
      <p><strong>Graves' disease</strong>: TSH receptor antibodies continuously stimulate the thyroid. Histology shows <b>tall columnar follicular cells</b>, <b>small follicles</b> with depleted colloid, and prominent <b>colloid scalloping</b> — essentially the appearance of a maximally active gland.</p>
      <p><strong>Hashimoto's thyroiditis</strong>: Autoimmune destruction of the thyroid. Histology shows <b>diffuse lymphocytic infiltration</b> with <b>germinal center formation</b>, follicle destruction, and <b class="purple-term">Hürthle cell metaplasia</b> — follicular cells that become large, granular, and intensely eosinophilic.</p>
      <p><strong>Simple (non-toxic) goiter</strong>: Thyroid enlargement due to iodine deficiency or increased hormone demand. Histology shows <b>enlarged follicles distended with abundant colloid</b> and flat follicular cells — the appearance of a maximally inactive gland.</p>
      <p><strong>Medullary thyroid carcinoma</strong>: Arises from C cells. Histology shows sheets of neoplastic cells with characteristic <b>amyloid deposits</b> in the stroma (amyloid derived from calcitonin precursors). Serum calcitonin is elevated and used as the tumor marker.</p>
      <div class="highlight-box">Clinical histology at a glance:
        <ul>
          <li>Graves' disease: <em>tall cells, small follicles, scalloped colloid</em></li>
          <li>Hashimoto's thyroiditis: <em>lymphocytic infiltration, germinal centers, Hürthle cells</em></li>
          <li>Simple goiter: <em>large follicles, abundant colloid, flat cells</em></li>
          <li>Medullary carcinoma: <em>C cell origin, amyloid in stroma, elevated calcitonin</em></li>
        </ul>
      </div>`,
    },
    {
      type: "question",
      text: "What histological finding is characteristic of Graves' disease?",
      options: [
        "Large follicles distended with smooth colloid",
        "Lymphocytic infiltration with germinal centers",
        "Tall columnar cells, small follicles, and scalloped colloid",
        "Amyloid deposits in the stroma",
      ],
      correct: 2,
      explanation:
        "Graves' disease is caused by TSH receptor antibodies that continuously stimulate the thyroid. Histologically this produces tall columnar follicular cells, small follicles with depleted colloid, and prominent colloid scalloping — the appearance of a maximally active gland.",
    },
    {
      type: "question",
      text: "What is the characteristic histological finding in Hashimoto's thyroiditis?",
      options: [
        "Tall columnar cells and scalloped colloid",
        "Lymphocytic infiltration with germinal centers and Hürthle cell metaplasia",
        "Large follicles distended with colloid",
        "Amyloid deposits and C cell hyperplasia",
      ],
      correct: 1,
      explanation:
        "Hashimoto's thyroiditis is autoimmune destruction of the thyroid. The hallmark histological findings are diffuse lymphocytic infiltration with germinal center formation, follicle destruction, and Hürthle cell metaplasia (large, granular, intensely eosinophilic follicular cells).",
    },
    {
      type: "question",
      text: "What histological feature is characteristic of medullary thyroid carcinoma?",
      options: [
        "Tall columnar follicular cells with scalloped colloid",
        "Lymphocytic infiltration and follicle destruction",
        "Amyloid deposits in the stroma",
        "Large follicles distended with eosinophilic colloid",
      ],
      correct: 2,
      explanation:
        "Medullary thyroid carcinoma arises from C cells and characteristically shows amyloid deposits in the stroma, derived from calcitonin precursors. Serum calcitonin is elevated and used as a tumor marker.",
    },
    {
      type: "question",
      text: "What is the cell of origin of medullary thyroid carcinoma?",
      options: [
        "Follicular cells",
        "Parafollicular cells (C cells)",
        "Endothelial cells",
        "Fibrocytes",
      ],
      correct: 1,
      explanation:
        "Medullary thyroid carcinoma arises from parafollicular C cells. Because these cells produce calcitonin, elevated serum calcitonin is used as a tumor marker. Amyloid deposits in the stroma are a characteristic histological finding.",
    },
    {
      type: "question",
      text: "A patient with long-standing iodine deficiency has an enlarged thyroid. What would you expect to see on histology?",
      options: [
        "Tall columnar cells, small follicles, scalloped colloid",
        "Lymphocytic infiltration with germinal centers",
        "Large follicles distended with abundant colloid and flat cuboidal cells",
        "Amyloid deposits in the stroma",
      ],
      correct: 2,
      explanation:
        "In a simple (non-toxic) goiter from iodine deficiency, the thyroid is understimulated and inactive. Histology shows large follicles engorged with abundant colloid, lined by flat cuboidal cells — the classic appearance of an inactive thyroid.",
    },
  ],
};
